import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { syncBlacklistToIndexedDB } from './blacklistService';
import { matchesSearchTerm } from './searchUtils';
import { isCacheStale, recordDataSync } from './cacheService';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Client Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== CLIENT DATA OPERATIONS =====

/**
 * Gets all clients with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} includeArchived - Whether to include archived clients
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of client objects
 */
export const getAllClients = async (includeArchived = false, forceRefresh = false) => {
  try {
    // If not forcing refresh, try IndexedDB first but check cache freshness
    if (!forceRefresh) {
      const cachedClients = await getClientsFromCache();
    
      if (cachedClients && cachedClients.length > 0) {
        // Check if cache is stale
        if (!isCacheStale('clients')) {
          
          // Deserialize dates from cached data
          const deserializedClients = cachedClients.map(client => deserializeDates(client));
          
          // Filter archived clients locally if needed
          let filteredClients = deserializedClients;
          if (!includeArchived) {
            filteredClients = deserializedClients.filter(client => !client.isArchived);
          }
          
          // Sort manually by most recent activity (created or updated) - newest first
          filteredClients.sort((a, b) => {
            const dateA = getMostRecentClientDate(a, false);
            const dateB = getMostRecentClientDate(b, false);
            return dateB - dateA;
          });
          
          return filteredClients;
        } else {
          }
      } else {
      }
    } else {
    }
    
    // Cache miss or force refresh - fetch from Firestore and update cache
    return await fetchAndCacheClients(includeArchived);
    
  } catch (error) {
    console.error('Error getting clients:', error);
    
    // Fallback to Firestore if IndexedDB fails
    try {
      return await fetchAndCacheClients(includeArchived);
    } catch (firestoreError) {
      console.error('Error fetching from Firestore:', firestoreError);
      return [];
    }
  }
};

/**
 * Forces refresh of clients data from Firestore, bypassing cache
 * @param {boolean} includeArchived - Whether to include archived clients
 * @returns {Promise<Array>} Fresh clients data
 */
export const refreshClientsData = async (includeArchived = false) => {
  try {
    return await fetchAndCacheClients(includeArchived);
  } catch (error) {
    console.error('Error refreshing clients data:', error);
    // Fallback to regular getAllClients with force refresh
    return await getAllClients(includeArchived, true);
  }
};

/**
 * Fetches clients from Firestore and updates IndexedDB cache
 * @param {boolean} includeArchived - Whether to include archived clients  
 * @returns {Promise<Array>} Array of client objects
 */
const fetchAndCacheClients = async (includeArchived = false) => {
  try {
    let q;
    if (includeArchived) {
      q = query(collection(db, 'clients'));
    } else {
      q = query(
        collection(db, 'clients'), 
        where('isArchived', '==', false)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const clientsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort manually by most recent activity (created or updated) - newest first
    clientsList.sort((a, b) => {
      const dateA = getMostRecentClientDate(a, true);
      const dateB = getMostRecentClientDate(b, true);
      return dateB - dateA;
    });
    
    // Update IndexedDB cache with fresh data
    await syncClientsToCache(clientsList);
    
    // Record cache sync time
    recordDataSync('clients');
    
    // Also sync blacklist incidents for offline access
    await syncBlacklistIncidents();
    
    return clientsList;
    
  } catch (error) {
    console.error('Error fetching clients from Firestore:', error);
    throw error;
  }
};

/**
 * Gets clients from IndexedDB cache
 * @returns {Promise<Array|null>} Cached clients or null if not available
 */
const getClientsFromCache = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => resolve(null);
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('clients')) {
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['clients'], 'readonly');
        const store = transaction.objectStore('clients');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const clients = getAllRequest.result;
          if (clients && clients.length > 0) {
            resolve(clients);
          } else {
            resolve(null); // Empty cache
          }
        };
        
        getAllRequest.onerror = () => resolve(null);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        // Create all required object stores
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('orders')) {
          db.createObjectStore('orders', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('technicians')) {
          db.createObjectStore('technicians', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('equipment_catalog')) {
          db.createObjectStore('equipment_catalog', { keyPath: 'id' });
        }
      };
    });
  } catch (error) {
    console.error('Error getting clients from cache:', error);
    return null;
  }
};

/**
 * Syncs clients data to IndexedDB cache
 * @param {Array} clients - Clients to cache
 * @returns {Promise<boolean>} Success status
 */
const syncClientsToCache = async (clients) => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        resolve(false);
      };
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('clients')) {
          console.warn('Clients object store does not exist');
          resolve(false);
          return;
        }
        
        const transaction = db.transaction(['clients'], 'readwrite');
        const store = transaction.objectStore('clients');
        
        // Clear existing data and add new data
        store.clear();
        clients.forEach(client => {
          store.put(client);
        });
        
        transaction.oncomplete = () => {
          conditionalLog(`Cached ${clients.length} clients to IndexedDB`);
          resolve(true);
        };
        
        transaction.onerror = () => {
          console.error('IndexedDB transaction error:', transaction.error);
          resolve(false);
        };
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' });
        }
      };
    });
  } catch (error) {
    console.error('Error syncing clients to cache:', error);
    return false;
  }
};

// ===== CLIENT USAGE CHECKING =====

/**
 * Checks which clients are used in orders (optimized version)
 * Uses cached orders data to avoid individual queries
 * @param {Array} clientIds - Client IDs to check
 * @returns {Promise<Set>} Set of client IDs that are in use
 */
export const getClientsInUse = async (clientIds) => {
  try {
    const inUseSet = new Set();
    
    // Try to get orders from IndexedDB cache first
    const cachedOrders = await getOrdersFromCache();
    
    if (cachedOrders && cachedOrders.length > 0) {
      conditionalLog('Using cached orders data to check client usage');
      
      // Check which clients are used in cached orders
      cachedOrders.forEach(order => {
        if (order.cliente && clientIds.includes(order.cliente)) {
          inUseSet.add(order.cliente);
        }
      });
      
      return inUseSet;
    }
    
    // Fallback: if no cached orders, do a single query for all orders
    conditionalLog('No cached orders found, querying Firestore for client usage check');
    const ordersQuery = query(collection(db, 'orders'));
    const ordersSnapshot = await getDocs(ordersQuery);
    
    ordersSnapshot.docs.forEach(doc => {
      const orderData = doc.data();
      if (orderData.cliente && clientIds.includes(orderData.cliente)) {
        inUseSet.add(orderData.cliente);
      }
    });
    
    return inUseSet;
    
  } catch (error) {
    console.error('Error checking client usage:', error);
    return new Set(); // Return empty set on error
  }
};

/**
 * Gets orders from IndexedDB cache (helper function)
 * @returns {Promise<Array|null>} Cached orders or null
 */
const getOrdersFromCache = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve) => {
      request.onerror = () => resolve(null);
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('orders')) {
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['orders'], 'readonly');
        const store = transaction.objectStore('orders');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result || null);
        };
        
        getAllRequest.onerror = () => resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting orders from cache:', error);
    return null;
  }
};

// ===== SEARCH FUNCTIONS =====

/**
 * Searches clients locally using IndexedDB cache
 * @param {string} searchTerm - Search term
 * @param {boolean} includeArchived - Whether to include archived clients
 * @returns {Promise<Array>} Filtered clients
 */
export const searchClientsLocal = async (searchTerm, includeArchived = false) => {
  try {
    if (!searchTerm.trim()) {
      return await getAllClients(includeArchived);
    }
    
    const clients = await getAllClients(includeArchived);
    
    return clients.filter(client => {
      // Search in company name
      if (client.nombreEmpresa && matchesSearchTerm(client.nombreEmpresa, searchTerm)) {
        return true;
      }
      
      // Search in contacts
      if (client.contactos && client.contactos.some(contact => {
        return (contact.nombre && matchesSearchTerm(contact.nombre, searchTerm)) ||
               (contact.telefono && contact.telefono.includes(searchTerm)) ||
               (contact.email && matchesSearchTerm(contact.email, searchTerm));
      })) {
        return true;
      }
      
      // Search in address
      if (client.direccion && matchesSearchTerm(client.direccion, searchTerm)) {
        return true;
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error searching clients locally:', error);
    return [];
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Gets the most recent date from createdAt and updatedAt
 * @param {Object} client - Client object with createdAt and updatedAt
 * @param {boolean} isFromFirestore - Whether dates are Firestore Timestamps
 * @returns {Date} Most recent date
 */
const getMostRecentClientDate = (client, isFromFirestore = false) => {
  let createdDate, updatedDate;
  
  if (isFromFirestore) {
    // Handle Firestore Timestamps
    createdDate = client.createdAt?.toDate ? client.createdAt.toDate() : new Date(client.createdAt || 0);
    updatedDate = client.updatedAt?.toDate ? client.updatedAt.toDate() : new Date(client.updatedAt || 0);
  } else {
    // Handle regular Date objects (from cache)
    createdDate = client.createdAt instanceof Date ? client.createdAt : new Date(client.createdAt || 0);
    updatedDate = client.updatedAt instanceof Date ? client.updatedAt : new Date(client.updatedAt || 0);
  }
  
  return createdDate > updatedDate ? createdDate : updatedDate;
};

/**
 * Converts serialized date objects back to Date objects
 * Handles Firestore Timestamps that have been serialized to IndexedDB
 * @param {Object} obj - Object that may contain serialized dates
 * @returns {Object} Object with dates converted to Date objects
 */
const deserializeDates = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    const value = obj[key];
    
    if (value && typeof value === 'object') {
      // Check if this looks like a serialized Firestore Timestamp
      if (value.seconds && typeof value.seconds === 'number') {
        result[key] = new Date(value.seconds * 1000);
      } else if (Array.isArray(value)) {
        result[key] = value.map(item => deserializeDates(item));
      } else {
        result[key] = deserializeDates(value);
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Gets display name for a client (matches existing logic)
 * @param {Object} client - Client object
 * @returns {string} Display name
 */
export const getClientDisplayName = (client) => {
  if (!client) return 'Cliente desconocido';
  
  const principalContact = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
  
  if (client.nombreEmpresa && principalContact?.nombre) {
    return `${client.nombreEmpresa} - ${principalContact.nombre}`;
  } else if (client.nombreEmpresa) {
    return client.nombreEmpresa;
  } else if (principalContact?.nombre) {
    return principalContact.nombre;
  }
  
  return 'Cliente sin nombre';
};

/**
 * Syncs blacklist incidents to IndexedDB for offline access
 * @returns {Promise<void>}
 */
const syncBlacklistIncidents = async () => {
  try {
    // Fetch all blacklist incidents from Firestore
    const blacklistQuery = query(collection(db, 'blacklist_incidents'));
    const blacklistSnapshot = await getDocs(blacklistQuery);
    
    const incidents = blacklistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sync to IndexedDB
    await syncBlacklistToIndexedDB(incidents);
    conditionalLog(`Synced ${incidents.length} blacklist incidents to cache`);
  } catch (error) {
    console.error('Error syncing blacklist incidents:', error);
    // Don't throw - this is optional for performance
  }
};